import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogFooter } from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/Auth";

export default function AddressForm({ address, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    id: address?.id || null,
    name: address?.name || "",
    address1: address?.address1 || "",
    address2: address?.address2 || "",
    city: address?.city || "",
    state: address?.state || "",
    zip: address?.zip || "",
    country: address?.country || "",
    isDefaultShipping: address?.isDefaultShipping || false,
    isDefaultBilling: address?.isDefaultBilling || false,
  });

  const { addAddress } = useAuth();

  useEffect(() => {
    if (address) {
      setFormData({
        ...address,
        isDefaultShipping: !!address.isDefaultShipping,
        isDefaultBilling: !!address.isDefaultBilling,
      });
    }
  }, [address]);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.id) {
        await addAddress(formData);
        onSubmit?.(); // <- Don't pass `res`
      } else {
        onSubmit?.(formData); // Still okay for edit
      }
    } catch {
      // Error handled in context
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Full Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-1"
        />
      </div>
      <div>
        <Label htmlFor="address1">Address Line 1 *</Label>
        <Input
          id="address1"
          value={formData.address1}
          onChange={handleChange}
          required
          className="mt-1"
        />
      </div>
      <div>
        <Label htmlFor="address2">Address Line 2 (Optional)</Label>
        <Input
          id="address2"
          value={formData.address2}
          onChange={handleChange}
          className="mt-1"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="city">City *</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="state">State / Province *</Label>
          <Input
            id="state"
            value={formData.state}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="zip">ZIP / Postal Code (Optional)</Label>
          <Input
            id="zip"
            value={formData.zip}
            onChange={handleChange}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="country">Country *</Label>
          <Input
            id="country"
            value={formData.country}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
      </div>
      <div className="flex items-center space-x-2 pt-2">
        <Checkbox
          id="isDefaultShipping"
          checked={formData.isDefaultShipping}
          onCheckedChange={(checked) =>
            handleChange({
              target: { id: "isDefaultShipping", type: "checkbox", checked },
            })
          }
        />
        <Label htmlFor="isDefaultShipping" className="text-sm">
          Set as default shipping address
        </Label>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="isDefaultBilling"
          checked={formData.isDefaultBilling}
          onCheckedChange={(checked) =>
            handleChange({
              target: { id: "isDefaultBilling", type: "checkbox", checked },
            })
          }
        />
        <Label htmlFor="isDefaultBilling" className="text-sm">
          Set as default billing address
        </Label>
      </div>
      <DialogFooter className="pt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="bg-primary hover:bg-primary/90 text-white"
        >
          {formData.id ? "Save Changes" : "Add Address"}
        </Button>
      </DialogFooter>
    </form>
  );
}
